import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Audio source configuration for multiple format support
 */
interface AudioSource {
  url: string;
  type: string;
}

/**
 * Configuration object for useOptimizedAudio hook
 */
interface AudioConfig {
  src: string | AudioSource[];
  preload?: 'none' | 'metadata' | 'auto';
  lazy?: boolean;
  volume?: number;
  loop?: boolean;
}

/**
 * Return type for useOptimizedAudio hook
 */
interface AudioControls {
  // State
  isLoading: boolean;
  hasError: boolean;
  isPlaying: boolean;
  isReady: boolean;
  duration: number;
  currentTime: number;
  
  // Controls
  play: () => Promise<void>;
  pause: () => void;
  stop: () => void;
  setVolume: (volume: number) => void;
  seekTo: (time: number) => void;
  
  // For manual initialization if lazy loading
  initialize: () => void;
}

/**
 * Custom hook for optimized audio loading and playback
 * 
 * Features:
 * - Lazy audio loading to reduce initial bundle size
 * - Preload control for better UX
 * - Error handling and retry logic
 * - Memory cleanup and performance optimization
 * - Support for multiple audio formats
 * 
 * @param config - Configuration object
 * @returns Audio loading state and controls
 */
export const useOptimizedAudio = ({
  src,
  preload = 'metadata',
  lazy = true,
  volume = 0.7,
  loop = false,
}: AudioConfig): AudioControls => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [isReady, setIsReady] = useState<boolean>(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timeUpdateRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize audio element
  useEffect(() => {
    if (!src || (lazy && !isReady)) return;

    const audio = new Audio();
    audioRef.current = audio;

    // Configure audio element
    audio.preload = preload;
    audio.volume = volume;
    audio.loop = loop;

    // Set source(s)
    if (Array.isArray(src)) {
      // Multiple sources for format fallback
      src.forEach((source: AudioSource) => {
        const sourceElement = document.createElement('source');
        sourceElement.src = source.url;
        sourceElement.type = source.type;
        audio.appendChild(sourceElement);
      });
    } else {
      audio.src = src;
    }

    // Event listeners
    const handleLoadStart = (): void => setIsLoading(true);
    const handleCanPlay = (): void => {
      setIsLoading(false);
      setIsReady(true);
      setHasError(false);
    };
    const handleError = (): void => {
      setIsLoading(false);
      setHasError(true);
      console.warn('Audio loading failed:', src);
    };
    const handleLoadedMetadata = (): void => {
      setDuration(audio.duration);
    };
    const handlePlay = (): void => setIsPlaying(true);
    const handlePause = (): void => setIsPlaying(false);
    const handleEnded = (): void => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      
      // Clean up audio element
      audio.pause();
      audio.src = '';
      audioRef.current = null;
    };
  }, [src, preload, volume, loop, lazy, isReady]);

  // Time update handler
  useEffect(() => {
    if (!audioRef.current || !isPlaying) return;

    const updateTime = (): void => {
      if (audioRef.current) {
        setCurrentTime(audioRef.current.currentTime);
      }
    };

    timeUpdateRef.current = setInterval(updateTime, 100);

    return () => {
      if (timeUpdateRef.current) {
        clearInterval(timeUpdateRef.current);
      }
    };
  }, [isPlaying]);

  // Play audio
  const play = useCallback(async (): Promise<void> => {
    if (!audioRef.current) {
      if (lazy && !isReady) {
        setIsReady(true);
        return;
      }
      return Promise.reject(new Error('Audio not ready'));
    }

    try {
      // Check if audio file exists before attempting to play
      if (audioRef.current && audioRef.current.readyState === 0) {
        // Audio hasn't loaded yet, wait for it
        await new Promise<void>((resolve, reject) => {
          const audio = audioRef.current;
          if (!audio) {
            reject(new Error('Audio element not available'));
            return;
          }

          const onCanPlay = (): void => {
            audio.removeEventListener('canplay', onCanPlay);
            audio.removeEventListener('error', onError);
            resolve();
          };
          const onError = (): void => {
            audio.removeEventListener('canplay', onCanPlay);
            audio.removeEventListener('error', onError);
            reject(new Error('Audio file not found or cannot be loaded'));
          };
          audio.addEventListener('canplay', onCanPlay);
          audio.addEventListener('error', onError);
          
          // Trigger loading if not already started
          if (audio.src && audio.readyState === 0) {
            audio.load();
          }
        });
      }
      
      await audioRef.current.play();
    } catch (error) {
      console.warn('Audio play failed:', error);
      setHasError(true);
      // Don't throw error - degrade gracefully
    }
  }, [lazy, isReady]);

  // Pause audio
  const pause = useCallback((): void => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
  }, []);

  // Stop audio (pause and reset to beginning)
  const stop = useCallback((): void => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
    }
  }, []);

  // Set volume
  const setVolumeLevel = useCallback((newVolume: number): void => {
    if (audioRef.current && newVolume >= 0 && newVolume <= 1) {
      audioRef.current.volume = newVolume;
    }
  }, []);

  // Seek to specific time
  const seekTo = useCallback((time: number): void => {
    if (audioRef.current && time >= 0 && time <= duration) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, [duration]);

  return {
    // State
    isLoading,
    hasError,
    isPlaying,
    isReady,
    duration,
    currentTime,
    
    // Controls
    play,
    pause,
    stop,
    setVolume: setVolumeLevel,
    seekTo,
    
    // For manual initialization if lazy loading
    initialize: () => setIsReady(true),
  };
};

export default useOptimizedAudio;